import { useEffect, useState } from 'react';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { acceptAttribute, UPLOAD_RULES, UploadPurpose } from '@/shared/api';
import { formatFileSize, formatPercent } from '@/shared/lib';
import { FormField } from '../form/FormField';
import {
  asText,
  type BaseFieldProps,
  describedBy,
  useBoundField,
  useFieldIds,
} from '../form/fieldBase';
import { useFormBusy } from '../form/formBusy';
import { Lightbox } from '../Lightbox';
import { UploadDropzone, type UploadedFile, UploadFileRow } from './UploadParts';
import { useFileUpload } from './useFileUpload';

export interface UploadFieldProps<TValues extends FieldValues> extends BaseFieldProps<TValues> {
  /**
   * Файл, сохранённый в записи, — показывается, пока значение поля равно исходному `file_id`
   * (ссылка и имя приходят в ответе сущности рядом с `*_file_id`).
   */
  current?: UploadedFile | null;
  /** Подпись в зоне выбора; по умолчанию — форматы и лимит назначения. */
  note?: string;
  /**
   * Файл загружен (значение поля — его `file_id`): имя исходного файла — для полей контракта
   * `file_name` (сервер имени загрузки не хранит).
   */
  onFileUploaded?: (source: File) => void;
}

interface UploadFieldBaseProps<TValues extends FieldValues> extends UploadFieldProps<TValues> {
  kind: 'image' | 'document';
  purpose: UploadPurpose;
}

/**
 * Поле загрузки одного файла для форм (COM-13). Значение поля — `file_id` (= `upload_id`
 * проверенного сервером файла) или null; имя поля = поле контракта (`*_file_id`), поэтому 422
 * ложатся в него без переименования. Пока идёт загрузка, FormDialog не отправляет форму.
 */
function UploadFieldBase<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide = true,
  disabled,
  current = null,
  note,
  onFileUploaded,
  kind,
  purpose,
}: UploadFieldBaseProps<TValues>) {
  const { t, i18n } = useTranslation('form');
  const ids = useFieldIds();
  const { clearErrors } = useFormContext<TValues>();
  const { field, fieldState } = useBoundField<TValues>(name);
  const value = asText(field.value);
  const { onChange } = field;
  const { state, start, reset } = useFileUpload({
    purpose,
    onUploaded: (file, source) => {
      onChange(file.upload_id);
      onFileUploaded?.(source);
    },
  });
  const [lightbox, setLightbox] = useState<{ src: string; name: string } | null>(null);
  useFormBusy(state.status === 'uploading');

  // Значение сброшено извне (новая форма, reset) — загруженный в этой форме файл больше не нужен.
  const uploadedId = state.result?.upload_id;
  useEffect(() => {
    if (uploadedId && value !== uploadedId) reset();
  }, [uploadedId, value, reset]);

  const rule = UPLOAD_RULES[purpose];
  const dropNote =
    note ??
    t('upload.note', {
      formats: t(`upload.formats.${rule.formats}`),
      max: formatFileSize(rule.maxSize, i18n.language),
    });
  const error = state.status === 'error' ? state.error : fieldState.error?.message;

  function remove() {
    reset();
    onChange(null);
  }

  let row = null;
  if (state.status === 'uploading' && state.file) {
    row = (
      <UploadFileRow
        kind={kind}
        name={state.file.name}
        previewUrl={state.previewUrl}
        chip={formatPercent(state.progress, i18n.language)}
        progress={state.progress}
        actionLabel={t('upload.cancel')}
        onAction={reset}
      />
    );
  } else if (state.status === 'done' && state.result && state.file && value) {
    const { file, result } = state;
    const src = state.previewUrl ?? result.url;
    row = (
      <UploadFileRow
        kind={kind}
        name={file.name}
        previewUrl={src}
        chip={formatFileSize(result.size ?? file.size, i18n.language)}
        actionLabel={t('upload.remove')}
        onAction={remove}
        actionDisabled={disabled}
        onOpen={kind === 'image' && src ? () => setLightbox({ src, name: file.name }) : undefined}
      />
    );
  } else if (value) {
    const src = current?.url ?? null;
    const shownName = current?.name || t('upload.currentFile');
    row = (
      <UploadFileRow
        kind={kind}
        name={shownName}
        previewUrl={src}
        chip={t('upload.currentChip')}
        actionLabel={t('upload.remove')}
        onAction={remove}
        actionDisabled={disabled}
        onOpen={kind === 'image' && src ? () => setLightbox({ src, name: shownName }) : undefined}
      />
    );
  }

  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      error={error ?? undefined}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <div className="flex flex-col gap-8">
        <UploadDropzone
          id={ids.id}
          accept={acceptAttribute(purpose)}
          title={t('upload.chooseFile')}
          note={dropNote}
          disabled={disabled || field.disabled}
          invalid={Boolean(error)}
          describedBy={describedBy(Boolean(error), Boolean(hint), ids)}
          onFile={(file) => {
            clearErrors(name);
            void start(file);
          }}
        />
        {row}
      </div>
      {lightbox ? (
        <Lightbox
          open
          onOpenChange={(open) => {
            if (!open) setLightbox(null);
          }}
          src={lightbox.src}
          title={lightbox.name}
        />
      ) : null}
    </FormField>
  );
}

export interface PurposeUploadFieldProps<TValues extends FieldValues>
  extends UploadFieldProps<TValues> {
  /** Назначение загрузки (COM-13): общее или модуля (`author_photo`, `onboarding_background`…). */
  purpose?: UploadPurpose;
}

/** Изображение: по умолчанию назначение `image` (JPG, PNG, WebP, HEIC — до 10 МБ). */
export function ImageUploadField<TValues extends FieldValues>({
  purpose = UploadPurpose.Image,
  ...props
}: PurposeUploadFieldProps<TValues>) {
  return <UploadFieldBase<TValues> {...props} kind="image" purpose={purpose} />;
}

/** Документ: по умолчанию назначение `document` (PDF — до 25 МБ). */
export function FileUploadField<TValues extends FieldValues>({
  purpose = UploadPurpose.Document,
  ...props
}: PurposeUploadFieldProps<TValues>) {
  return <UploadFieldBase<TValues> {...props} kind="document" purpose={purpose} />;
}
